/** What the routing policy decided, and a way to find one card. */

import { memo, useCallback, useEffect, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Button,
  Caption1,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { ArrowClockwiseRegular, PlayRegular, StopRegular } from '@fluentui/react-icons'
import {
  Chip,
  DataTableShell,
  EmptyState,
  FilterBar,
  FreshnessChip,
  IDChip,
  KeyValueList,
  KeyValueRow,
  LoadingState,
  MonoText,
  StatusBadge,
  formatTimestamp,
  useDetailDrawer,
} from '../platform'
import { api, stream } from '../api'
import {
  belongsInLiveList,
  liveIsMeaningful,
  decisionsRequest,
  type DecisionFilters,
} from './decisionFilters'
import { summaryRequest } from './decisionFilters'
import { fromInstant } from './searchQuery'
import type { DecisionSummary, DecisionTotals as Totals, Page } from '../api'
import { DecisionTotals } from './DecisionTotals'
import {
  CANARY_LINE,
  KNOWN_DECIDERS,
  dcsAnsweredInWords,
  dcsAskedInWords,
  decidedByLabel,
  identifierKindInWords,
  laneLabel,
  readingOf,
  reasonInWordsFor,
} from './decisionReading'

/** The window the page keeps open. A stream left running over a weekend stays
 *  a window on the data, never a second copy of it. */
const MAX_ROWS = 200

const OUTCOME_STATUS = {
  APPROVED: 'success',
  BLOCKED: 'error',
  UNKNOWN: 'warning',
} as const

const OUTCOMES = ['APPROVED', 'BLOCKED', 'UNKNOWN'] as const

type Preset = 'hour' | 'day' | 'week' | 'custom'

const PRESETS: [Exclude<Preset, 'custom'>, string][] = [
  ['hour', 'Last hour'],
  ['day', '24 hours'],
  ['week', '7 days'],
]

const PRESET_MS: Record<Exclude<Preset, 'custom'>, number> = {
  hour: 3_600_000,
  day: 86_400_000,
  week: 7 * 86_400_000,
}

/** The start of a preset window, as the From box holds it. */
function presetFrom(preset: Exclude<Preset, 'custom'>): string {
  return fromInstant(new Date(Date.now() - PRESET_MS[preset]).toISOString())
}

function scopeOf(preset: Preset, outcome: string | null): string {
  const window =
    preset === 'custom' ? 'the range chosen' : PRESETS.find(([value]) => value === preset)![1].toLowerCase()
  const which = outcome ? `outcome ${outcome}` : 'every outcome'
  return `${preset === 'hour' ? 'the last hour' : preset === 'custom' ? window : `the last ${window}`}, ${which}`
}

// The schemes this service classifies an identifier into, and the four of them
// that are BMW. Listed rather than derived from the rows on screen, for the
// reason the lanes are: a filter offering only what is already here cannot be
// used to find out that something is missing.
const SCHEMES = [
  'BMW_PERSONAL_10',
  'BMW_PERSONAL_20',
  'BMW_FLEET_21',
  'BMW_EBEREICH_22',
  'EMAID',
  'OTHER',
] as const
const BMW_SCHEMES = SCHEMES.filter((scheme) => scheme.startsWith('BMW_'))

// The lanes and the bases, as chips beside the outcomes. Listed rather than
// derived from what is on screen: a filter offering only the values in the
// rows it already holds cannot be used to find out that a value is missing,
// which is the question somebody has when a lane goes quiet.
const AUTHORITIES = ['tim', 'dcs', 'hubject'] as const
export const BASES = [
  'authority_answer',
  'authority_no_verdict',
  'authority_unavailable',
  'policy_denied',
  'estate_unknown',
  'stamp_not_written',
  // The four endings of a Hubject dial (DEC-23, 3b). Four and not one because
  // they differ in whether a session can be held upstream that this service
  // holds no id for, and this filter is how somebody finds those rows.
  'authority_timed_out_upstream',
  'authority_deadline_passed',
  'not_asked_no_budget',
  'authority_accepted_no_session',
  // The Wall-E list decided and nobody was asked (DEC-28).
  'wall_e_list',
] as const

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  searchGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    maxWidth: '100%',
    minWidth: 0,
    '@media (max-width: 719px)': {
      width: 'calc(100vw - 80px)',
    },
  },
  searchForm: {
    width: '340px',
    maxWidth: '100%',
    minWidth: 0,
    '@media (max-width: 719px)': {
      width: '100%',
    },
  },
  searchFilters: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
  },
  search: {
    width: '100%',
    minWidth: 0,
  },
  row: {
    cursor: 'pointer',
    '@media (max-width: 719px)': {
      height: '44px',
    },
  },
  nowrap: {
    whiteSpace: 'nowrap',
  },
  // MEASURED RATHER THAN CHOSEN. The widest value each column actually holds,
  // at 1,280 and 1,440 with every outcome and both station shapes on screen:
  // the timestamp needs 173 px and had 190, the charge point needs 130 and had
  // 160. The 37 px between them pays for the outcome column below, which was
  // too narrow for its own badge.
  timeColumn: {
    width: '178px',
  },
  stationColumn: {
    width: '135px',
  },
  // THE BADGE WAS WIDER THAN ITS CELL. Measured: the cell held 93 px of content
  // space, BLOCKED needs 84 and fits, UNKNOWN needs 92 and APPROVED 93, so both
  // painted 6 to 7 px past the cell and stopped 2 px short of the reason beside
  // them: "UNKNOWN" ran into "DCS does not know the card" as one phrase. The
  // column was never given a width, so it was sized by the narrowest badge the
  // layout could get away with.
  outcomeColumn: {
    width: '100px',
  },
  // SIZED ON THE LONGEST VALUE IN THE AGREED SET, which is not the longest
  // value this table had ever shown.
  //
  // It said 140 "because TIM_BMW_FLEET is the longest value". That was true of
  // the data on screen when it was measured and false of the vocabulary: the
  // agreed set in `logic/token_class.py` also holds DCS_BMW_CORPORATE,
  // DCS_BMW_EMPLOYEE and EROAMING_HUBJECT, and the first of those is 45 px
  // wider than TIM_BMW_FLEET. It also renders in the proportional font, where
  // the muted monospace fallback OTHER uses is narrower per character, so a
  // column sized by eye on OTHER and TIM_BMW_FLEET is sized on the two
  // narrowest things it will ever hold.
  //
  // Found on prod, on the first screenshot after a real DCS approval arrived.
  // Measured at 1,440 with all three in rows: the cell held 156 px, the ink of
  // DCS_BMW_CORPORATE is 160, so it painted 12 px past the cell and the gap to
  // the identifier beside it was MINUS 4. The two read as one word,
  // "DCS_BMW_CORPORATE36fcea533683", which is the same defect the identifier
  // column's own comment describes, arriving from the other side.
  //
  // 166 gives that value 22 px of clear space, which is what Identifier and
  // Decided by have. It costs nobody: the table is pinned at 1,170 and the
  // latency column takes the remainder, so the 26 px comes out of its 65 px of
  // slack and leaves it 39.
  classColumn: {
    width: '166px',
    // THE CLIP IS FOR A VALUE NOBODY HAS AGREED YET, not for these.
    //
    // No agreed value truncates at this width, which is the point: an ellipsis
    // would be worse than useless on these, because DCS_BMW_CORPORATE and
    // DCS_BMW_EMPLOYEE differ only at the END. Clipping the tail makes the two
    // read identically, and they are different cost centres.
    //
    // So it never fires on the set as agreed, and when the Hub agrees a longer
    // one it degrades to a clip rather than to a collision, with the full value
    // on hover.
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // THE INDEX CANNOT SPILL INTO THE COLUMN BESIDE IT. Measured at 1,024, 1,280
  // and 1,440: the cell was 83 px holding 86 px of monospace, so the text ran
  // 11 px past where "Decided by" starts and the two read as one word,
  // "a1b2c3d4e5f6Service OCPI".
  //
  // The clip is the guard and not the repair. Once every other column was cut
  // to what its widest value actually measures, this one has 108 px for 102 px
  // of text, so nothing clips today and the index sits 13 px clear. The rule
  // stays because the table's total is fixed by the shell: a column that grows
  // takes its space from here, and this is where that has to stop.
  //
  // THE TOKEN IS WIDER THAN THE INDEX IT REPLACED (2026-10-17). Measured at
  // 1,440 with real rows: the cell was 95 px, a 14 character card needs 117
  // and a hyphenated eMAID 146, so both were cut at about ten characters, on
  // the one value the change exists to show. 134 plus padding fits the eMAID.
  // The 30 px come out of Why, which truncates by design and has the whole
  // reading in the drawer, and the rest out of the latency column's slack.
  identifierColumn: {
    width: '134px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // One line, because the values are short names and wrapping them is what
  // the browser showed: "nobody / was / asked" over three lines beside a
  // header reading "Decided / by". Measured at 1,440, where the column was
  // sized by its contents and the contents are the narrowest thing on the row.
  deciderColumn: {
    width: '130px',
    whiteSpace: 'nowrap',
  },

  // The authority's own words, which can be long. The column holds one line
  // and the full text is on hover, so a wordy refusal cannot push every other
  // column out of shape.
  whyColumn: {
    // 176 rather than 190, measured: the table needed 1,170 px and the card
    // holds 1,166 at a 1,440 viewport, so it scrolled by four pixels and the
    // latency column sat behind a scrollbar on the most common screen in the
    // office. Then 146 (2026-10-17), giving 30 px to the token beside it: the
    // token is the value Lucian asked to see, and this line is a summary of
    // the three sentences in the drawer, truncated by design with the whole
    // line on hover.
    width: '146px',
    maxWidth: '146px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  drawer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  drawerAction: {
    alignSelf: 'flex-start',
  },
  // Eight columns of identifiers and timestamps do not fit a narrow window.
  // The shell body scrolls, so the table keeps its columns legible instead of
  // crushing them into each other.
  table: {
    width: '1170px',
    minWidth: '1170px',
    tableLayout: 'fixed',
  },
})

export default function Decisions() {
  const styles = useStyles()
  const drawer = useDetailDrawer<DecisionSummary>()
  const [rows, setRows] = useState<DecisionSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  // OPENED ALREADY FILTERED, which is how a row on Attempts gets here. Read
  // once, at mount: afterwards the controls own the state, so a reader who
  // clears the card is not overruled by the address they arrived on.
  const [params] = useSearchParams()
  const [blindIndex, setBlindIndex] = useState(
    () => params.get('identifier_blind_index') ?? '',
  )
  const [card, setCard] = useState('')
  const [outcome, setOutcome] = useState<string | null>(null)
  const [basis, setBasis] = useState<string | null>(null)
  const [authority, setAuthority] = useState<string | null>(null)
  const [decidedBy, setDecidedBy] = useState<string | null>(null)
  const [canary, setCanary] = useState(false)
  const [reason, setReason] = useState('')
  const [schemes, setSchemes] = useState<string[]>(
    () => (params.get('scheme') ?? '').split(',').filter(Boolean),
  )
  const [station, setStation] = useState(() => params.get('station_id') ?? '')
  // A WINDOW FROM THE START, 24 HOURS, so the totals above the list and the
  // list itself answer the same question (DEC-28 C1). A link that names a
  // range keeps it, as before.
  const named = params.get('from') !== null || params.get('to') !== null
  const [preset, setPreset] = useState<Preset>(named ? 'custom' : 'day')
  const [from, setFrom] = useState(() =>
    named ? fromInstant(params.get('from')) : presetFrom('day'),
  )
  const [to, setTo] = useState(() => fromInstant(params.get('to')))
  const [totals, setTotals] = useState<Totals | null>(null)
  const [cursor, setCursor] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [live, setLive] = useState(false)
  const [arrived, setArrived] = useState(0)

  const filters: DecisionFilters = {
    identifier: card || undefined,
    blindIndex: blindIndex || undefined,
    schemes,
    outcome,
    basis,
    authority,
    decidedBy,
    reason: reason || undefined,
    stationId: station || undefined,
    from: from || undefined,
    to: to || undefined,
    canary,
  }

  // Both narrowings are the service's to do. A page that filters the rows it
  // already holds answers "are there refusals among these", and the question
  // is "are there refusals": with the refusal older than the window, the first
  // answers no and is wrong.
  const load = useCallback((active: DecisionFilters, after?: string | null) => {
    // `after` is what separates the two things this does. Without one the page
    // replaces its rows; with one the page grew and the new rows go on the
    // end. One function, so a filter change cannot append to the answers to a
    // question nobody is asking any more.
    if (after) setLoadingMore(true)
    const asked = decisionsRequest(active, after)
    ;(asked.method === 'post'
      ? api.post<Page<DecisionSummary>>(asked.path, asked.body)
      : api.get<Page<DecisionSummary>>(asked.path)
    )
      .then((page) => {
        setRows((current) => (after ? [...(current ?? []), ...page.items] : page.items))
        setCursor(page.next_cursor ?? null)
        setError(null)
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoadingMore(false))
  }, [])

  // The totals of exactly what the list is filtered by, asked beside it.
  const loadTotals = useCallback((active: DecisionFilters) => {
    const asked = summaryRequest(active)
    ;(asked.method === 'post'
      ? api.post<Totals>(asked.path, asked.body)
      : api.get<Totals>(asked.path)
    )
      // An answer of another shape is a service a deploy behind or ahead: the
      // list is the page, and totals it cannot read leave it standing.
      .then((answer) => setTotals(Array.isArray(answer?.buckets) ? answer : null))
      .catch(() => setTotals(null))
  }, [])

  useEffect(() => {
    loadTotals(filters)
  }, [loadTotals, outcome, basis, authority, decidedBy, reason, blindIndex, schemes, from, to, canary]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Keyed on the outcome and not on `card` on purpose: picking a filter
    // requeries, typing a card number does not, because a search runs when the
    // form is submitted. `card` is read here rather than tracked so that
    // changing the filter keeps the card already searched for.
    // No cursor: a new question starts at its first page. Carrying one over
    // would answer the new filter from wherever the old list happened to stop.
    load(filters, null)
  }, [load, outcome, basis, authority, decidedBy, reason, blindIndex, schemes, from, to, canary]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!live) return
    // A closed range cannot gain members, so a live badge over it would read
    // as a service that has stopped rather than a filter doing its job.
    if (!liveIsMeaningful(filters)) return
    // New decisions go on top. Rows are keyed on the correlation id and the row
    // itself is memoised, so an arrival inserts one row and leaves every other
    // row, and the reader's scroll position, untouched.
    return stream<DecisionSummary>(
      '/decisions/stream',
      (decision) => {
        // The stream carries everything, so the filter has to hold here too.
        // Otherwise watching refusals live quietly fills with approvals, and
        // the list stops meaning what its filter says it means.
        if (!belongsInLiveList(filters, decision)) return
        setArrived((n) => n + 1)
        setRows((current) => [decision, ...(current ?? [])].slice(0, MAX_ROWS))
      },
      setError,
    )
  }, [live, outcome, basis, authority, decidedBy, reason, schemes, station, to, canary]) // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <EmptyState title="The service is not answering" description={error} />

  const shown = rows ?? []

  return (
    <div className={styles.page}>
      <FilterBar
        search={
          <div className={styles.searchGroup}>
            <form
              className={styles.searchForm}
              onSubmit={(e) => {
                e.preventDefault()
                setLive(false)
                load(filters, null)
                loadTotals(filters)
              }}
            >
              <Input
                className={styles.search}
                value={card}
                onChange={(_, data) => setCard(data.value)}
                placeholder="Full token, as the call centre gives it"
                aria-label="Find a card"
              />
            </form>
            <div className={styles.searchFilters}>
              {/* WHY THIS LIST IS SHORT, said on the page. Arriving from
                  Attempts filters to one card, and a narrowed list with
                  nothing saying so is the console answering a question the
                  reader did not ask. Clicking it clears the filter. */}
              {blindIndex ? (
                <Chip selected onClick={() => setBlindIndex('')}>
                  one card: {blindIndex.slice(0, 12)}
                </Chip>
              ) : null}
              {OUTCOMES.map((value) => (
                <Chip
                  key={value}
                  selected={outcome === value}
                  onClick={() => setOutcome(outcome === value ? null : value)}
                >
                  {value}
                </Chip>
              ))}
              {AUTHORITIES.map((value) => (
                <Chip
                  key={value}
                  selected={authority === value}
                  onClick={() => setAuthority(authority === value ? null : value)}
                >
                  {value}
                </Chip>
              ))}
              {BASES.map((value) => (
                <Chip
                  key={value}
                  selected={basis === value}
                  onClick={() => setBasis(basis === value ? null : value)}
                >
                  {value === 'wall_e_list' ? 'Wall-E list' : value.replace('authority_', '').replace('_', ' ')}
                </Chip>
              ))}
              {/* Canaries alone or none (DEC-29): the checks of a revision
                  before it takes traffic are never mixed with the taps. */}
              <Chip selected={canary} onClick={() => setCanary(!canary)}>
                Canary
              </Chip>
            </div>
            {/* Who decided, on its own row: it is a different question from the
                lane, and putting the two in one row is how they got confused
                in the first place. Listed rather than derived from the rows on
                screen, for the reason the lanes are: a filter offering only
                what is already here cannot be used to find out that a decider
                has gone quiet. */}
            {/* THE ONE CLICK FIND-45 TURNS ON, first in the row because it is
                a question rather than a value: every BMW scheme that took the
                default route. Not empty means a BMW card reached Service OCPI,
                which does not know BMW badge numbers, so the session was
                refused there and settles to nobody. */}
            <div className={styles.searchFilters}>
              <Chip
                selected={isLeakPreset(schemes, authority)}
                onClick={() => {
                  if (isLeakPreset(schemes, authority)) {
                    setSchemes([])
                    setAuthority(null)
                    return
                  }
                  setSchemes([...BMW_SCHEMES])
                  setAuthority('dcs')
                }}
              >
                BMW cards on the default route
              </Chip>
              {SCHEMES.map((value) => (
                <Chip
                  key={value}
                  selected={schemes.includes(value)}
                  onClick={() =>
                    setSchemes(
                      schemes.includes(value)
                        ? schemes.filter((scheme) => scheme !== value)
                        : [...schemes, value],
                    )
                  }
                >
                  {value}
                </Chip>
              ))}
            </div>
            <div className={styles.searchFilters}>
              {KNOWN_DECIDERS.map((value) => (
                <Chip
                  key={value}
                  selected={decidedBy === value}
                  onClick={() => setDecidedBy(decidedBy === value ? null : value)}
                >
                  {decidedByLabel(value)}
                </Chip>
              ))}
            </div>
            <form
              className={styles.searchForm}
              onSubmit={(e) => {
                e.preventDefault()
                setLive(false)
                load(filters, null)
              }}
            >
              <Input
                className={styles.search}
                value={station}
                onChange={(_, data) => setStation(data.value)}
                placeholder="Charge point id"
                aria-label="Find a charge point"
              />
            </form>
            <form
              className={styles.searchForm}
              onSubmit={(e) => {
                e.preventDefault()
                setLive(false)
                load(filters, null)
              }}
            >
              {/* ITS OWN FORM, AND NOT FOR TIDINESS. A form with two text
                  inputs and no submit button has no default button, so Enter
                  stops submitting it: adding this box beside the charge point
                  one broke that filter, and the paging test said so. One input
                  per form is what keeps Enter working on both. */}
              <Input
                className={styles.search}
                value={reason}
                onChange={(_, data) => setReason(data.value)}
                placeholder="Reason, exactly as the authority sent it. For example work_charger_rejected"
                aria-label="Find a reason"
              />
            </form>
            <div className={styles.searchFilters}>
              {/* Both ends, and the service treats them as inclusive. Typed
                  rather than picked from a calendar because an investigation
                  usually starts from a timestamp somebody has in front of
                  them, copied off a row or out of a message. */}
              {PRESETS.map(([value, label]) => (
                <Chip
                  key={value}
                  selected={preset === value}
                  onClick={() => {
                    setPreset(value)
                    setFrom(presetFrom(value))
                    setTo('')
                  }}
                >
                  {label}
                </Chip>
              ))}
              <Input
                type="datetime-local"
                value={from}
                onChange={(_, data) => {
                  setPreset('custom')
                  setFrom(data.value)
                }}
                aria-label="From"
              />
              <Input
                type="datetime-local"
                value={to}
                onChange={(_, data) => {
                  setPreset('custom')
                  setTo(data.value)
                }}
                aria-label="To"
              />
            </div>
          </div>
        }
        actions={
          <>
            <Button
              icon={<ArrowClockwiseRegular />}
              onClick={() => load(filters, null)}
              disabled={live}
            >
              Refresh
            </Button>
            <Button
              icon={live ? <StopRegular /> : <PlayRegular />}
              appearance={live ? 'primary' : 'secondary'}
              onClick={() => {
                setArrived(0)
                setLive(!live)
              }}
            >
              {live ? 'Stop' : 'Live'}
            </Button>
          </>
        }
      />

      {totals ? (
        <DecisionTotals
          totals={totals}
          scope={scopeOf(preset, outcome)}
          outcome={outcome}
          onOutcome={setOutcome}
          onReason={setReason}
        />
      ) : null}

      <DataTableShell
        title="Decisions"
        description="Which token the station sent, who was asked, what they answered, and what this service answered. To find one card, type its token as the call centre gives it."
        controls={<FreshnessChip state={live ? 'live' : 'paused'} />}
        footerStart={`${shown.length} ${shown.length === 1 ? 'decision' : 'decisions'} loaded`}
        footerEnd={live ? `${arrived} arrived while watching` : null}
      >
        {!rows ? (
          <LoadingState label="Reading decisions" />
        ) : shown.length === 0 ? (
          <EmptyState
            title="No decisions to show"
            description={
              outcome
                ? `The service holds no decision recorded as ${outcome}.`
                : 'Nothing has been presented in this window.'
            }
          />
        ) : (
          <Table size="small" className={styles.table}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell className={styles.timeColumn}>Time, Berlin</TableHeaderCell>
                <TableHeaderCell className={styles.stationColumn}>Charge point</TableHeaderCell>
                <TableHeaderCell className={styles.classColumn}>Card type</TableHeaderCell>
                <TableHeaderCell className={styles.identifierColumn}>Token</TableHeaderCell>
                {/* ROUTE IS NOT HERE, AND IT WAS. Nine columns did not fit:
                    measured at 1,440 they collided, with the route printing
                    over the identifier and the latency pushed off the right
                    edge. Which rule matched is a question asked about one
                    decision, and it is in the drawer and on the full page; who
                    decided is asked while scanning, which is what this table
                    is for. */}
                <TableHeaderCell className={styles.deciderColumn}>Decided by</TableHeaderCell>
                <TableHeaderCell className={styles.outcomeColumn}>Outcome</TableHeaderCell>
                <TableHeaderCell className={styles.whyColumn}>Why</TableHeaderCell>
                <TableHeaderCell>Latency</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((decision) => (
                <DecisionRow
                  key={decision.correlation_id}
                  decision={decision}
                  onOpen={drawer.open}
                />
              ))}
            </TableBody>
          </Table>
        )}
        {/* The end of the list says which end it is. Without this a reader
            cannot tell a page that happens to hold everything from one that
            stopped at fifty, and the second is what the console did for every
            question ever asked of it. */}
        {shown.length > 0 ? (
          <div className={styles.searchFilters}>
            {cursor ? (
              <Button onClick={() => load(filters, cursor)} disabled={loadingMore}>
                {loadingMore ? 'Loading…' : `Load more, ${shown.length} so far`}
              </Button>
            ) : (
              <Caption1>{`All ${shown.length} that match are shown.`}</Caption1>
            )}
          </div>
        ) : null}
      </DataTableShell>
    </div>
  )
}

/** Memoised on purpose: an arrival on the live stream must insert a row, not
 *  repaint the table under the reader's cursor. */
export const DecisionRow = memo(function DecisionRow({
  decision,
  onOpen,
}: {
  decision: DecisionSummary
  onOpen: (decision: DecisionSummary) => void
}) {
  const styles = useStyles()
  return (
    <TableRow
      className={styles.row}
      tabIndex={0}
      aria-label={`Open decision from ${decision.station_id}`}
      onClick={() => onOpen(decision)}
      onKeyDown={(event) => openRowFromKeyboard(event, () => onOpen(decision))}
    >
      <TableCell className={mergeClasses(styles.nowrap, styles.timeColumn)}>
        <MonoText>{formatTimestamp(decision.occurred_at) || 'no time recorded'}</MonoText>
      </TableCell>
      <TableCell className={styles.stationColumn}>
        <MonoText>{decision.station_id}</MonoText>
      </TableCell>
      <TableCell className={styles.classColumn} title={decision.token_class ?? undefined}>
        {decision.token_class ? (
          decision.token_class
        ) : (
          // No class is known, so the routing scheme stands in and says so by
          // being muted. The two are different vocabularies and the drawer
          // keeps them apart; here the reader wants one label.
          <MonoText tone="muted">{decision.scheme}</MonoText>
        )}
      </TableCell>
      {/* The token where it was kept (2026-10-17), and the blind index,
          muted, where it was not: the index still follows the card. */}
      <TableCell
        className={styles.identifierColumn}
        title={decision.identifier ?? decision.identifier_blind_index}
      >
        {decision.identifier ? (
          <MonoText>{decision.identifier}</MonoText>
        ) : (
          <MonoText tone="muted">{decision.identifier_blind_index.slice(0, 12)}</MonoText>
        )}
      </TableCell>
      {/* Who decided, which the lane does not say. Muted where nobody recorded
          it, so a column of "not recorded" during the rollout reads as absent
          rather than as an answer. */}
      <TableCell className={styles.deciderColumn}>
        {decision.decided_by ? (
          decidedByLabel(decision.decided_by)
        ) : (
          <MonoText tone="muted">not recorded</MonoText>
        )}
      </TableCell>
      <TableCell className={styles.outcomeColumn}>
        <StatusBadge status={OUTCOME_STATUS[decision.outcome]} label={decision.outcome} />
      </TableCell>
      <TableCell className={styles.whyColumn}>
        {/* Only where it carries something. An approval reads
            "authority_answer", which is noise in a column whose whole purpose
            is to make the refusals readable without opening each one. */}
        {decision.outcome === 'APPROVED' ? null : (
          // The sentence, with the authority's own words underneath it on
          // hover. A reader scanning for the work-charger refusals needs the
          // first; a reader about to raise it with Service OCPI needs the
          // second, verbatim.
          // Without the decider's name: it has its own column one cell to
          // the left, and repeating it here spends the width that makes the
          // reason readable.
          <span title={decision.reason ?? decision.basis}>
            {reasonInWordsFor(decision)}
          </span>
        )}
      </TableCell>
      <TableCell className={styles.nowrap}>
        <MonoText>{decision.latency_ms} ms</MonoText>
      </TableCell>
    </TableRow>
  )
})

function openRowFromKeyboard(event: KeyboardEvent, open: () => void) {
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  open()
}

/**
 * What goes inside the drawer for one row. AppShell owns the chrome (rule 9).
 *
 * A quick look, with the full page one click away: "which authority answered"
 * is usually answered here, and the wire view is not.
 */
export function DecisionDrawerBody({ decision }: { decision: DecisionSummary }) {
  const styles = useStyles()
  const navigate = useNavigate()
  const drawer = useDetailDrawer<DecisionSummary>()
  const reading = readingOf(decision)
  return (
    <div className={styles.drawer}>
      <KeyValueList divided>
        {/* WHAT HAPPENED, FIRST AND IN PLAIN WORDS (2026-10-17): what arrived,
            who was asked and what they said, what this service answered. The
            facts underneath are the same story in their raw words. */}
        <KeyValueRow label="What arrived" value={reading.arrived} />
        <KeyValueRow label="Who was asked" value={reading.asked} />
        <KeyValueRow label="What this service answered" value={reading.answered} />
        <KeyValueRow
          label="Occurred, Berlin"
          value={formatTimestamp(decision.occurred_at) || 'no time recorded'}
        />
        <KeyValueRow
          label="Charge point"
          value={<MonoText>{decision.station_id}</MonoText>}
        />
        {/* Two vocabularies for the same card, kept apart on purpose. Scheme is
            how this service's routing predicate classified the number; card
            class is what the Integration Hub and the OCPP Server call it
            (DEC-17). The list shows one label, this shows both. */}
        <KeyValueRow label="Scheme" value={decision.scheme} />
        <KeyValueRow
          label="Card class"
          value={decision.token_class ?? 'none is known for this decision'}
        />
        <KeyValueRow
          label="Token"
          value={decision.identifier ? <MonoText>{decision.identifier}</MonoText> : 'not kept'}
        />
        <KeyValueRow
          label="Blind index"
          value={<MonoText tone="muted">{decision.identifier_blind_index}</MonoText>}
        />
        <KeyValueRow label="Route" value={decision.route} />
        {/* The lane, named as the lane. It reads 'dcs' in the record for every
            answer on the default route, including the ones DCS never saw, and
            showing that word made the page claim DCS answered (FIND-43). */}
        {decision.canary ? <KeyValueRow label="Canary" value={CANARY_LINE} /> : null}
        <KeyValueRow label="Lane" value={laneLabel(decision.authority)} />
        <KeyValueRow label="Decided by" value={decidedByLabel(decision.decided_by)} />
        <KeyValueRow label="DCS asked" value={dcsAskedInWords(decision.dcs_called)} />
        {/* Shown whenever DCS was asked, because "asked: yes" without the
            answer leaves the two readings that matter indistinguishable: a
            card DCS refused, and a card nobody could ask about. */}
        {decision.dcs_called ? (
          <KeyValueRow
            label="DCS answered"
            value={dcsAnsweredInWords(decision.dcs_response, decision.occurred_at)}
          />
        ) : null}
        <KeyValueRow label="Card issuer" value={decision.issuer ?? 'not recorded'} />
        <KeyValueRow
          label="Kind, as Service OCPI said"
          value={identifierKindInWords(decision.id_token_kind)}
        />
        <KeyValueRow
          label="Outcome"
          value={<StatusBadge status={OUTCOME_STATUS[decision.outcome]} label={decision.outcome} />}
        />
        <KeyValueRow label="Basis" value={decision.basis} />
        {/* What was said, next to which kind of answer it was. FIND-02 is what
            happens when only the second is on the page. */}
        <KeyValueRow
          label="Reason"
          value={decision.reason ?? 'the authority gave none'}
        />
        <KeyValueRow label="Latency" value={`${decision.latency_ms} ms`} />
      </KeyValueList>
      <Button
        className={styles.drawerAction}
        appearance="primary"
        onClick={() => {
          drawer.close()
          navigate(`/decisions/${decision.correlation_id}`)
        }}
      >
        Open the full decision
      </Button>
    </div>
  )
}

/** Handed to AppShell so the one drawer it owns knows what a decision looks
 *  like. Every value is passed explicitly: the toolkit renders an em-dash for
 *  an omitted one, and programme writing rules reach the UI too (rule 11). */
export const decisionDrawer = {
  // Overlay, not inline: opening one decision must not reflow the table the
  // reader was looking at.
  variant: 'overlay' as const,
  renderTitle: (decision: DecisionSummary) => decision.station_id,
  renderSubtitle: (decision: DecisionSummary) => (
    <IDChip value={decision.correlation_id} tone="brand" copyable />
  ),
  render: (decision: DecisionSummary) => <DecisionDrawerBody decision={decision} />,
}

/**
 * Whether the preset is what is currently asked for.
 *
 * Compared as a set rather than by length: four chips that happen to number
 * four is not the same question, and a chip that looks pressed while asking
 * something else is worse than no chip.
 */
export function isLeakPreset(schemes: string[], authority: string | null): boolean {
  if (authority !== 'dcs') return false
  const asked = new Set(schemes)
  return (
    asked.size === BMW_SCHEMES.length && BMW_SCHEMES.every((scheme) => asked.has(scheme))
  )
}
